<!--
 * @Author: Nemo
 * @Description: 账单信息表单组件
 * @Date: 2024-05-29
-->
<template>
  <div class="billing-form">
    <div class="billing-title-text">{{ $t('message.checkout.billingAddress') }}</div>
    <el-form ref="billingForm" :model="formData" :rules="formRules" label-position="top" :hide-required-asterisk="true"
      class="billing-form-el-form">
      <el-row :gutter="24" class="billing-form-row">
        <template v-for="(field, index) in formFields">
          <!-- 特殊处理 firstName 和 lastName 显示在同一行，共用一个标题 -->
          <template v-if="field.name === 'firstName'">
            <el-col :key="index" :xs="24" :sm="24" :md="24" :lg="24" :xl="24">
              <!-- 共用的联系人姓名标题 -->
              <div class="form-label name-label">
                <span>{{ $t('message.checkout.contactName') }}</span>
                <span
                  v-show="judgeRequired('firstName', formData['firstName']) || judgeRequired('lastName', formData['lastName'])"
                  class="required-tip">
                  {{ $t('message.validator.required') }}
                </span>
              </div>

              <!-- 名字输入框行 -->
              <el-row :gutter="24">
                <el-col :xs="12" :sm="12" :md="12" :lg="12" :xl="12">
                  <el-form-item :prop="field.name" :ref="`${field.name}Ref`" class="no-label-item">
                    <el-input v-model="formData[field.name]" :placeholder="getFieldPlaceholder(field.name)"
                      :maxlength="getFieldMaxLength(field.name)" :type="getFieldType(field.name)"
                      :autocomplete="getFieldAutocomplete(field.name)" @focus="handleFieldFocus(field.name)"
                      @change="handleFieldChange(field.name)" @input="handleFieldInput(field.name)" />
                  </el-form-item>
                </el-col>

                <!-- 如果formFields中包含lastName，则在firstName旁显示 -->
                <el-col v-if="formFields.some(f => f.name === 'lastName')" :xs="12" :sm="12" :md="12" :lg="12" :xl="12">
                  <el-form-item prop="lastName" :ref="`lastNameRef`" class="no-label-item">
                    <el-input v-model="formData['lastName']" :placeholder="getFieldPlaceholder('lastName')"
                      :maxlength="getFieldMaxLength('lastName')" :type="getFieldType('lastName')"
                      :autocomplete="getFieldAutocomplete('lastName')" @focus="handleFieldFocus('lastName')"
                      @change="handleFieldChange('lastName')" @input="handleFieldInput('lastName')" />
                  </el-form-item>
                </el-col>
              </el-row>
            </el-col>
          </template>

          <!-- 跳过lastName，因为已经在firstName部分渲染 -->
          <template v-else-if="field.name !== 'lastName'">
            <el-col :key="index" :xs="24" :sm="field.fullWidth ? 24 : 12" :md="field.fullWidth ? 24 : 12"
              :lg="field.fullWidth ? 24 : 12" :xl="field.fullWidth ? 24 : 12">
              <el-form-item :prop="field.name" :ref="`${field.name}Ref`">
                <template slot="label">
                  <div class="form-label">
                    <span>{{ getFieldLabel(field.name) }}</span>
                    <span v-show="judgeRequired(field.name, formData[field.name])" class="required-tip">
                      {{ $t('message.validator.required') }}
                    </span>
                  </div>
                </template>
                <!-- 特殊处理国家字段为禁用下拉框 -->
                <template v-if="field.name === 'country'">
                  <el-select disabled filterable v-model="formData[field.name]" autocomplete="new-password"
                    auto-complete="new-password">
                    <el-option v-for="(c, cindex) in COUNTRY_OPTIONS" :key="cindex" :value="c.code"
                      :label="$t(`message.country.${c.code}`)">
                    </el-option>
                  </el-select>
                </template>
                <!-- 特殊处理省份/州字段，根据国家类型选择下拉框或输入框 -->
                <template v-else-if="field.name === 'province'">
                  <!-- 国家为【US/CA/CN】时为select,其它为input -->
                  <el-select v-if="selectStateCountry.includes(formData.country)" filterable
                    v-model="formData[field.name]" @visible-change="handleSelectChange($event, field.name)"
                    @change="handleFieldChange(field.name)">
                    <el-option v-for="(s, sindex) in STATES_OPTIONS[formData.country]" :key="sindex" :value="s.value"
                      :label="$t(`message.state.${formData.country}.${s.value}`)">
                    </el-option>
                  </el-select>
                  <el-input v-else v-model="formData[field.name]" :maxlength="getFieldMaxLength(field.name)"
                    :type="getFieldType(field.name)" :autocomplete="getFieldAutocomplete(field.name)"
                    @focus="handleFieldFocus(field.name)" @change="handleFieldChange(field.name)"
                    @input="handleFieldInput(field.name)" />
                </template>
                <!-- 正常处理其他字段 -->
                <template v-else>
                  <el-input v-model="formData[field.name]" :placeholder="getFieldPlaceholder(field.name)"
                    :maxlength="getFieldMaxLength(field.name)" :type="getFieldType(field.name)"
                    :autocomplete="getFieldAutocomplete(field.name)" @focus="handleFieldFocus(field.name)"
                    @change="handleFieldChange(field.name)" @input="handleFieldInput(field.name)" />
                </template>
              </el-form-item>
            </el-col>
          </template>
        </template>
      </el-row>
    </el-form>
  </div>
</template>

<script>
import COUNTRY_OPTIONS from '@/utils/tools/country';
import STATES_OPTIONS from '@/utils/tools/state';
import { fieldConfigurationRulesAndNamesHandler, showMessage, billingFieldsSort, selectStateCountry } from '@/views/checkout/utils/utils.js';
import { allRules } from '@/views/checkout/utils/validator.js';
import { deviceFingerprintManager } from '@/plugins/eventTrackManager/deviceFingerprintManager';

export default {
  name: 'billing-form',
  props: {
    fields: {
      type: Array,
      default: () => [],
      required: true,
    },
    country: {
      type: String,
      default: '',
    },
    orderInfo: {
      type: Object,
      default: () => ({}),
    },
    paymentType: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      formData: {},
      formRules: {},
      isValidateRequired: false,
      fieldNames: [],
      filteredFields: [],
      COUNTRY_OPTIONS,
      STATES_OPTIONS,
      selectStateCountry,
    };
  },
  computed: {
    formFields() {
      // 仅返回过滤并排序后的字段，不自动补充默认字段
      return this.filteredFields;
    },
  },
  watch: {
    fields: {
      immediate: true,
      handler(newFields) {
        this.initForm(newFields);
      },
    },
    country(newCountry) {
      if (newCountry && this.formData.country !== newCountry) {
        this.formData.country = newCountry;
      }
    },
  },
  methods: {
    // 初始化表单数据和验证规则
    initForm(newFields) {
      // 过滤并排序字段
      const displayFields = newFields.filter(field => field.display === 'Y');

      // 使用billingFieldsSort函数进行排序
      billingFieldsSort(displayFields);
      this.filteredFields = displayFields;

      // 初始化表单数据
      const formData = {};
      const formRules = {};

      // 预处理字段值
      displayFields.forEach(field => {
        formData[field.name] = field.value || '';
      });

      // 使用utils.js中的方法处理验证规则
      this.fieldNames = fieldConfigurationRulesAndNamesHandler(
        displayFields,
        formRules,
        this.country || formData.country || 'US',
      );

      // 如果有国家字段，初始化为props传入的country
      if (this.country && formData.country === '') {
        formData.country = this.country;
      }

      this.formData = formData;
      this.formRules = formRules;
    },

    // 激活必填规则验证 - 点击提交按钮时调用
    setRequiredRule() {
      try {
        this.isValidateRequired = true;
        const requiredRules = {};

        for (const key in this.formRules) {
          if (this.formRules[key]) {
            requiredRules[key] = this.formRules[key].map(item => {
              return { ...item, required: item.name === 'required' };
            });
          }
        }

        this.formRules = { ...requiredRules };
        return true;
      } catch (error) {
        return false;
      }
    },

    // 判断是否显示必填标记 - 参考index.vue的实现
    judgeRequired(fieldName, value) {
      try {
        return this.formRules[fieldName]?.some(item => item.name === 'required' && this.isValidateRequired) && !value;
      } catch (error) {
        return false;
      }
    },

    validate() {
      return new Promise((resolve, reject) => {
        // 先激活必填规则
        this.setRequiredRule();

        // 使用$nextTick确保规则更新后再验证
        this.$nextTick(() => {
          this.$refs.billingForm.validate(valid => {
            if (!valid) {
              // 收集校验错误并发送埋点
              this.trackValidationFailed();
              // Element UI验证失败
              reject(new Error('表单验证失败'));
              return;
            }

            // Element UI验证通过，但我们还需要手动检查必填字段
            let allFieldsValid = true;
            let invalidField = '';

            // 检查所有必填字段
            for (const fieldName in this.formRules) {
              if (this.formRules[fieldName].some(rule => rule.name === 'required') && !this.formData[fieldName]) {
                allFieldsValid = false;
                invalidField = fieldName;
                break;
              }
            }

            if (allFieldsValid) {
              resolve(this.getFormData());
            } else {
              // 必填字段验证失败，发送埋点
              this.trackValidationFailed();
              reject(new Error(`字段 ${invalidField} 验证失败`));
            }
          });
        });
      });
    },

    /**
     * 收集所有校验错误并发送埋点
     */
    trackValidationFailed() {
      try {
        const errors = { required: [], format: [] };

        // 收集表单错误
        this.$refs.billingForm.fields.forEach(field => {
          if (field.validateState === 'error') {
            const value = this.formData[field.prop];
            (value ? errors.format : errors.required).push(field.prop);
          }
        });

        // 发送埋点
        if (errors.required.length || errors.format.length) {
          // 转换支付类型名称
          let payment = this.paymentType || 'BILLING';
          if (payment === 'apple') {
            payment = 'ApplePay';
          } else if (payment === 'google') {
            payment = 'GooglePay';
          }

          deviceFingerprintManager.send({
            e: 'validation_failed',
            lmo: JSON.stringify({ ...errors, payment }),
            m: this.orderInfo?.merchantTxnId || '',
            kd: this.orderInfo?.merchantNo || '',
            k: this.orderInfo?.key || '',
            ct: 'AGGREGATE',
            co: this.orderInfo?.txnCurrency || '',
            at: this.orderInfo?.txnAmount || '',
            gwq: this.orderInfo?.txnId || '',
          });
        }
      } catch (err) {
        console.error('收集账单表单校验错误失败:', err);
      }
    },

    getFormData() {
      return this.formData;
    },

    getFieldLabel(fieldName) {
      const labelMap = {
        firstName: this.$t('message.checkout.firstName'),
        lastName: this.$t('message.checkout.lastName'),
        email: this.$t('message.checkout.email'),
        phone: this.$t('message.checkout.phone'),
        address: this.$t('message.checkout.address'),
        city: this.$t('message.checkout.city'),
        province: this.$t('message.checkout.province'),
        country: this.$t('message.checkout.country'),
        postalCode: this.$t('message.checkout.postalCode'),
        street: '街道号',
        number: '门牌号',
      };

      return labelMap[fieldName] || fieldName;
    },

    getFieldPlaceholder(fieldName) {
      return this.getFieldLabel(fieldName);
    },

    getFieldMaxLength(fieldName) {
      const maxLengthMap = {
        address: 256,
        email: 64,
        phone: 32,
        postalCode: 32,
        firstName: 64,
        lastName: 64,
        city: 64,
        province: 64,
      };

      return maxLengthMap[fieldName] || 64;
    },

    getFieldType(fieldName) {
      return fieldName === 'phone' ? 'tel' : 'text';
    },

    getFieldAutocomplete(fieldName) {
      const autocompleteMap = {
        address: 'street-address',
        email: 'email',
        phone: 'tel',
        postalCode: 'postal-code',
        firstName: 'given-name',
        lastName: 'family-name',
        city: 'address-level2',
        province: 'address-level1',
      };

      return autocompleteMap[fieldName] || 'off';
    },

    // 处理特殊字段的输入格式化
    handleFieldInput(fieldName) {
      if (fieldName === 'postalCode') {
        this.onPostalCodeInput();
      } else if (fieldName === 'identityNumber') {
        this.onIdentityNumberInput();
      }
    },

    handleFieldFocus(fieldName) {
      this.$emit('field-focus', fieldName);
    },

    handleFieldChange(fieldData) {
      this.$emit('field-change', {
        name: typeof fieldData === 'string' ? fieldData : fieldData.name,
        value: typeof fieldData === 'string' ? this.formData[fieldData] : fieldData.value,
      });
    },

    // 处理select下拉框的事件
    handleSelectChange(opened, fieldName) {
      if (opened) {
        this.handleFieldFocus(fieldName);
      }
    },

    // 邮政编码输入处理 - 参照index.vue的实现
    onPostalCodeInput() {
      const country = this.formData.country;
      const value = this.formData.postalCode;
      if (country === 'US' && value) {
        if (value.length === 9 && value.indexOf('-') === -1) {
          const newValue = `${value.slice(0, 5)}-${value.slice(5, value.length)}`;
          this.formData.postalCode = newValue;
        }
      }
    },

    // 身份证号输入处理 - 参照index.vue的实现
    onIdentityNumberInput() {
      const country = this.formData.country;
      const value = this.formData.identityNumber;
      if (country === 'BR' && value) {
        let newValue = value.replace(/[^\d]/g, '');
        if (newValue.length === 11) {
          newValue = `${newValue.slice(0, 3)}.${newValue.slice(3, 6)}.${newValue.slice(6, 9)}-${newValue.slice(9, 11)}`;
        } else if (newValue.length === 14) {
          newValue = `${newValue.slice(0, 2)}.${newValue.slice(2, 5)}.${newValue.slice(5, 8)}/${newValue.slice(8, 12)}-${newValue.slice(12, 14)}`;
        }
        this.formData.identityNumber = newValue;
      }
    },

    resetFields() {
      this.isValidateRequired = false;
      this.$refs.billingForm.resetFields();
    },
  },
};
</script>

<style lang="scss" scoped>
.billing-form {
  // margin-bottom: 20px;

  .billing-title-text {
    font-family: "Open Sans regular";
    font-weight: 500;
    font-size: 16px;
    color: #1A202C;
    line-height: 20px;
    // padding-top: 10px;
    padding-bottom: 16px;
    box-sizing: border-box;
    font-weight: bold;
    border-bottom: 1px solid #EDF1F7;
    margin-bottom: 24px;
    text-align: left;
  }
}

.form-label {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: "Open Sans regular";
  font-weight: 400;
  font-size: 14px;
  color: #1A202C;
  line-height: 18px;
  text-align: left;
  font-style: normal;
  text-transform: none;
  margin-bottom: 6px;

  .required-tip {
    font-weight: 400;
    font-size: 14px;
    color: #F53F3F;
    line-height: 18px;
    text-align: right;
    font-style: normal;
    text-transform: none;
  }
}

.name-label {
  margin-bottom: 6px;
}

.no-label-item {
  :deep(.el-form-item__label) {
    display: none;
  }
}

:deep(.el-input__inner) {
  background: #FFFFFF;
  box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  border: 1px solid #D0D5DD;
  height: 44px;

  &:focus {
    border-color: #182058 !important;
    box-shadow: 0px 0px 0px 3px #F1F1FF !important;
  }
}

:deep(.el-form-item) {
  margin-bottom: 24px;
}

:deep(.el-form--label-top .el-form-item__label) {
  font-family: "Open Sans regular";
  padding: 0 0 6px;
  font-size: 14px !important;
  color: #1A202C;
}

.billing-form-row {
  display: flex;
  flex-wrap: wrap;
}
</style>
